import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  resolveProductName,
  resolveProductPrice,
  getMewsProducts,
} from '@/services/platform/data/booking/mews';
import type { MewsProductDto } from '@/services/platform/data/booking/mews';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ProgressDots } from '@/components/shared/progressDots';
import { cn } from '@/lib/core/utils/cn';
import { ImageMap, formatShortDate } from '@/lib/core/utils';
import { ImageWithCrops } from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core/i18n';
import { BookingDetailBanner } from '@/components/shared/booking/common/detail/banner';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type { MewsMoreDetailsProps } from './types';
import { SUNBED_LABELS, type MewsFlowLabels } from '../labels';
import type { MewsBookingState } from '@/services/platform/data/booking/mews/types';

// ── Summary content ───────────────────────────────────────────────────────────

function SummaryContent({
  booking,
  selectedExtras,
  products,
  priceUnitLabel,
  whenLabel,
  labels,
}: {
  booking: MewsBookingState;
  selectedExtras: string[];
  products: MewsProductDto[];
  priceUnitLabel?: string;
  whenLabel?: string;
  labels: MewsFlowLabels;
}) {
  const t = useClientTranslation();
  const selectedExtraNames = products
    .filter(p => p.id && selectedExtras.includes(p.id))
    .map(p => resolveProductName(p));

  return (
    <div className="flex flex-col gap-4 py-4">
      {/* WHERE — only rendered when a venue is set (auto-filled from the single-venue list; hidden when empty) */}
      {booking.venue?.name && (
        <div className="flex items-start gap-8 border-b border-[#dfdfdf] pb-4">
          <p className="font-pp-neue-montreal w-35 shrink-0 text-[12px] tracking-[0.5px] text-black uppercase">
            {t(labels.summaryWhere)}
          </p>
          <p className="font-pp-neue-montreal flex-1 text-right text-[14px] text-[#1a1a1a]">
            {booking.venue.name}
          </p>
        </div>
      )}

      {/* WHEN */}
      {booking.dateFrom && booking.dateTo && (
        <div className="flex items-start gap-8 border-b border-[#dfdfdf] pb-4">
          <p className="font-pp-neue-montreal w-35 shrink-0 text-[12px] tracking-[0.5px] text-black uppercase">
            {t(labels.summaryWhen)}
          </p>
          <p className="font-pp-neue-montreal flex-1 text-right text-[14px] text-[#1a1a1a]">
            {whenLabel ??
              (booking.dateFrom.toDateString() === booking.dateTo.toDateString()
                ? formatShortDate(booking.dateFrom)
                : `${formatShortDate(booking.dateFrom)} - ${formatShortDate(booking.dateTo)}`)}
          </p>
        </div>
      )}

      {/* HOW MANY */}
      <div className="flex items-start gap-8 border-b border-[#dfdfdf] pb-4">
        <p className="font-pp-neue-montreal w-35 shrink-0 text-[12px] tracking-[0.5px] text-black uppercase">
          {t(labels.summaryHowMany)}
        </p>
        <p className="font-pp-neue-montreal flex-1 text-right text-[14px] text-[#1a1a1a]">
          {t(labels.summaryGuestsAdults, { count: booking.adults })}
          {booking.children > 0 ? ` ${t(labels.summaryGuestsChildren, { count: booking.children })}` : ''}
        </p>
      </div>

      {/* WHICH CATEGORY */}
      <div className="flex items-start gap-8 border-b border-[#dfdfdf] pb-4">
        <p className="font-pp-neue-montreal w-35 shrink-0 text-[12px] tracking-[0.5px] text-black uppercase">
          {t(labels.summaryWhichCategory)}
        </p>
        <div className="flex-1 text-right">
          {booking.allocations.map((a, idx) => {
            const totalAdults = a.guestDistribution.reduce((s, g) => s + g.adults, 0);
            const totalChildren = a.guestDistribution.reduce((s, g) => s + g.children, 0);
            return (
              <div key={a.categoryId} className={cn(idx > 0 && 'mt-3')}>
                <p className="font-pp-neue-montreal text-[14px] text-[#1a1a1a]">
                  x{a.quantity} {a.categoryName}
                </p>
                <p className="font-pp-neue-montreal text-[14px] text-[#848484]">
                  {t(labels.guestBreakdown, { adults: totalAdults, children: totalChildren })}
                </p>
                <p className="font-pp-neue-montreal text-[14px] text-[#848484]">
                  {a.pricePerDay} € {priceUnitLabel ?? t(labels.priceUnit)}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ADD-ONS */}
      {selectedExtraNames.length > 0 && (
        <div className="flex items-start gap-8 border-b border-[#dfdfdf] pb-4">
          <p className="font-pp-neue-montreal w-35 shrink-0 text-[12px] tracking-[0.5px] text-black uppercase">
            {t(labels.summaryAddOns)}
          </p>
          <div className="flex-1 text-right">
            {selectedExtraNames.map(name => (
              <p key={name} className="font-pp-neue-montreal text-[14px] text-[#1a1a1a]">
                {name}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Pricing ───────────────────────────────────────────────────────────────────

/**
 * Sum of allocations (quantity × pricePerDay) and selected, non-`alwaysIncluded` extras. Exported
 * so each flow's `handleBookNow` can charge exactly what this screen displays, without
 * re-fetching or re-deriving `products` itself.
 */
export function computeMewsGrandTotal(booking: MewsBookingState, products: MewsProductDto[]): number {
  const extrasTotal = products
    .filter(p => !p.alwaysIncluded && p.id && booking.extras.includes(p.id))
    .reduce((s, p) => s + resolveProductPrice(p), 0);
  const allocationTotal = booking.allocations.reduce((s, a) => s + a.quantity * a.pricePerDay, 0);
  return allocationTotal + extrasTotal;
}

// ── MewsMoreDetails ───────────────────────────────────────────────────────────────

export function MewsMoreDetails({
  cmsProps,
  booking,
  onBookingChange,
  onBack,
  onContinue,
  isSubmitting,
  priceUnitLabel,
  whenLabel,
  labels = SUNBED_LABELS,
  summaryRowVariant,
}: MewsMoreDetailsProps) {
  const t = useClientTranslation();
  const { locale } = useIntl();

  const [products, setProducts] = useState<MewsProductDto[]>([]);
  useEffect(() => {
    getMewsProducts(
      {
        CurrencyCode: 'EUR',
        ...(booking.venue?.externalId ? { EnterpriseId: booking.venue.externalId } : {}),
        ...(booking.venue?.bookableServiceExternalId
          ? { ServiceId: booking.venue.bookableServiceExternalId }
          : {}),
      },
      locale
    ).then(data => setProducts(data ?? []));
  }, [locale, booking.venue?.externalId, booking.venue?.bookableServiceExternalId]);
  const selectableProducts = products.filter(p => !p.alwaysIncluded);

  const selectedExtras = booking.extras;

  const grandTotal = computeMewsGrandTotal(booking, products);

  const totalUnits = booking.allocations.reduce((s, a) => s + a.quantity, 0);
  const totalGuests = booking.adults + booking.children;
  const heroDate =
    whenLabel ??
    (booking.dateFrom && booking.dateTo && booking.dateFrom.toDateString() !== booking.dateTo.toDateString()
      ? `${formatShortDate(booking.dateFrom)} - ${formatShortDate(booking.dateTo)}`
      : booking.dateFrom
        ? formatShortDate(booking.dateFrom)
        : '');

  const venueImage = ImageMap(booking.venue?.image as ImageWithCrops, 'bookingDetailHero');
  const venueLogo = ImageMap(booking.venue?.logo as ImageWithCrops);

  const toggleExtra = (id: string) => {
    const next = selectedExtras.includes(id) ? selectedExtras.filter(e => e !== id) : [...selectedExtras, id];
    onBookingChange({ extras: next });
  };

  return (
    <div className="relative flex h-full flex-col bg-white">
      {/* Header — fixed overlay on top of banner */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-99 -mt-[max(1rem,env(safe-area-inset-top))]!">
        <div className="pt-safe-area pointer-events-auto flex items-center justify-between px-4 py-2">
          <button
            type="button"
            aria-label={t('App.Labels.Back')}
            className="flex size-11 cursor-pointer items-center justify-center"
            onClick={onBack}
          >
            <Icon name="long-arrow-up-left" width={24} height={24} strokeWidth={2} className="text-white" />
          </button>
          <ProgressDots step={3} light />
        </div>
      </div>

      {/* Scrollable area — banner + content scroll together */}
      <div className="flex-1">
        {/* Banner */}
        <BookingDetailBanner
          image={venueImage}
          logo={venueLogo}
          rows={[
            [
              ...(heroDate ? [{ text: heroDate }] : []),
              ...(totalUnits > 0
                ? [
                    {
                      icon: 'sea-and-sun',
                      text: `${totalUnits} ${t(labels.unit, { count: totalUnits })}`,
                    },
                  ]
                : []),
            ],
            [
              ...(selectedExtras.length > 0
                ? [
                    {
                      icon: 'hexagon-plus',
                      text: `${selectedExtras.length} ${selectedExtras.length === 1 ? t(labels.addOn) : t(labels.addOns)}`,
                    },
                  ]
                : []),
              {
                icon: 'group',
                text: `${totalGuests} ${totalGuests === 1 ? t('App.Labels.Guest') : t('App.Labels.Guests')}`,
              },
            ],
          ]}
          style={{
            marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top))',
            paddingTop: 'calc(112px + max(1rem, env(safe-area-inset-top))',
          }}
        />

        {/* Expandable content */}
        <div className="flex flex-col gap-6 px-4 pt-10 pb-[calc((env(safe-area-inset-bottom)+1.5rem)+112px)]">
          <Accordion type="multiple" className="w-full">
            {/* ADD EXTRA SERVICES */}
            <AccordionItem value="extras">
              <AccordionTrigger>
                {cmsProps?.extraServicesLabel ?? t('App.Labels.AddExtraServices')}
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="flex flex-col">
                  {selectableProducts.map(product => {
                    const id = product.id!;
                    const name = resolveProductName(product);
                    const price = resolveProductPrice(product);
                    const isSelected = selectedExtras.includes(id);
                    return (
                      <label
                        key={id}
                        htmlFor={id}
                        className="flex cursor-pointer items-center gap-3 border-b-[0.5px] border-[#4e4e4e] py-2.5"
                      >
                        <Checkbox id={id} checked={isSelected} onCheckedChange={() => toggleExtra(id)} />
                        <p className="font-pp-neue-montreal min-w-0 flex-1 text-[12px] tracking-[0.5px] text-[#1a1a1a] uppercase">
                          {name}
                        </p>
                        <div className="flex shrink-0 flex-col items-end tracking-[0.5px]">
                          <p className="font-pp-neue-montreal text-[12px] text-[#1a1a1a] uppercase">
                            {price} €
                          </p>
                          {product.chargingMode && (
                            <p className="font-inter text-[10px] font-medium text-[#848484]">
                              {product.chargingMode.toUpperCase()}
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}

                  {/* Special requests */}
                  <div
                    className={cn('flex flex-col gap-2', summaryRowVariant === 'compact' ? 'py-2' : 'py-4.5')}
                  >
                    <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-black uppercase">
                      {t(labels.specialRequests)}
                    </p>
                    <input
                      type="text"
                      placeholder={t(labels.specialRequestPlaceholder)}
                      value={booking.notes}
                      onChange={e => onBookingChange({ notes: e.target.value })}
                      className="font-pp-neue-montreal w-full border-b-[0.5px] border-[#d1d1d1] py-2 text-[12px] tracking-[0.5px] text-[#1a1a1a] placeholder:text-[#848484] placeholder:uppercase focus:outline-none"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* VIEW BOOKING SUMMARY */}
            <AccordionItem value="summary">
              <AccordionTrigger>{t(labels.viewBookingSummary)}</AccordionTrigger>
              <AccordionContent>
                <SummaryContent
                  booking={booking}
                  selectedExtras={selectedExtras}
                  products={products}
                  priceUnitLabel={priceUnitLabel}
                  whenLabel={whenLabel}
                  labels={labels}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Promotion code */}
          <div className="flex flex-col gap-1">
            <p
              className={cn(
                'font-inter text-[14px] font-medium tracking-[0.5px]',
                summaryRowVariant === 'compact'
                  ? 'text-[#1a1a1a]'
                  : 'text-jncquoi-neutral-1000 leading-normal'
              )}
            >
              {t(labels.promotionCode)}
            </p>
            <input
              type="text"
              placeholder={t(labels.promotionCodePlaceholder)}
              value={booking.voucherCode}
              onChange={e => onBookingChange({ voucherCode: e.target.value })}
              className="font-pp-neue-montreal h-10 w-full border-[0.5px] border-[#dfdfdf] px-3.75 py-2 text-[14px] text-[#1a1a1a] placeholder:text-[#a9a9a9] focus:outline-none"
            />
          </div>

          {/* Edit booking */}
          <button
            type="button"
            onClick={onBack}
            className="font-pp-neue-montreal flex h-13 w-full cursor-pointer items-center justify-center rounded-[2px] p-4 text-[12px] tracking-[0.5px] text-[#1a1a1a] uppercase"
          >
            {t(labels.editBooking)}
          </button>
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 w-full shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-[calc((env(safe-area-inset-bottom)+42px))]">
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="primary-solid"
            palette="dark"
            size="lg"
            className="max-w-68 flex-1"
            onClick={() => onContinue(grandTotal)}
            disabled={isSubmitting}
          >
            {cmsProps?.bookNowButtonLabel ?? t('App.Labels.BookNow')}
          </Button>
          <div className="shrink-0 text-right">
            <p className="font-pp-neue-montreal text-[14px] font-semibold text-[#1a1a1a]">{grandTotal} €</p>
            <p className="font-pp-neue-montreal text-[12px] text-[#666]">{t('App.Labels.TaxesIncluded')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
